import React, { useEffect, useState, useRef } from "react";
import { useCamara } from "@/components/Camara";
import { prepararArchivo } from "@/lib/imagen";
import { abrirVisor } from "@/lib/visor";
import { hoyISO, isoLocal, sumarDias } from "@/lib/zona";
import { fechaLarga } from "@/lib/fechas";
import { catLabel } from "@/mock/data";
import { Camera, Upload, Mail, BellRing, Edit3, Receipt, X, FileCheck2, Info, ShieldAlert, AlertTriangle, PawPrint, Link2, Home, Files, Eye, Loader2, HelpCircle, User, Car, Plus, Ticket, Contact, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useData } from "@/context/DataContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { dec2, eur, parseImporte } from "@/lib/gastos";
import { matchPets, computePetUpdates } from "@/lib/petMatch";
import { matchViviendas, computeViviendaUpdates, extractViviendaData, hasViviendaData } from "@/lib/viviendaMatch";
import { matchVehiculos, hasVehiculoData } from "@/lib/vehiculoMatch";
import { ElegirAnalisis } from "@/components/documentos/PreguntaSubida";
import { IA_DOCS_PAUSADA } from "@/lib/pausas";
import { esTecnico, nombrePropuesto } from "@/lib/nombres";
import { HoraAviso } from "@/components/HoraAviso";
import { useNavigate } from "react-router-dom";
import { PetUpdateProposal } from "@/components/PetUpdateProposal";
import { buscarDuplicado, cuandoTxt, mensajeGuardado } from "@/lib/asuntoGuardado";
import { PersonaChip, ConAvatar } from "@/components/personas/Avatar";
import { SugerenciaGasto } from "@/components/gastos/SugerenciaGasto";
import { sugerirPersona } from "@/components/ocio/PersonaPlan";
import { useConfirmarNoClinico } from "@/components/documentos/ConfirmarNoClinico";
import { OcioAdd } from "@/components/ocio/OcioAdd";
import { RecordatorioPuntual } from "@/components/asuntos/RecordatorioPuntual";
import { CorreoForm } from "@/components/documentos/CorreoForm";
import { nombreRel } from "@/pages/Familia";
import { ContactoDetectado, esContacto, OpcionesContacto, ContactoYaGuardado } from "@/components/documentos/ContactoDetectado";

// Solo avisos cuya fecha (vencimiento − días) aún no ha pasado.
const avisosFuturos = (due, opts) => (due ? opts.filter((o) => sumarDias(due, -o) > hoyISO()) : opts);

const CATEGORIES = ["Hogar", "Vehículos", "Contratos y seguros", "Mascotas", "Otros"];
const CONF = 0.6;
const AVISO_OPTS = [30, 15, 7, 3, 1];
const fechaDMA = (iso) => iso.split("-").reverse().join("/");
const PRIORIDADES = [
  { id: "alta", label: "Alta" },
  { id: "normal", label: "Normal" },
  { id: "baja", label: "Baja" },
];

const OPTIONS = [
  { id: "photo", label: "Hacer una foto", desc: "Fotografía una carta, una factura o un recibo", Icon: Camera },
  { id: "galeria", label: "Elegir de la galería", desc: "Una foto que ya tengas en el móvil", Icon: ImageIcon, soloMovil: true },
  { id: "upload", label: "Subir documento", desc: "Selecciona un PDF o imagen del dispositivo", Icon: Upload },
  { id: "correo", label: "Correo", desc: "Pega un correo que hayas recibido", Icon: Mail },
  { id: "manual", label: "Introducir manualmente", desc: "Escribe tú mismo los datos o el recordatorio", Icon: Edit3 },
  { id: "recordatorio", label: "Recordatorio", desc: "Una fecha concreta, con hora si quieres", Icon: BellRing },
  { id: "contacto", label: "Contacto", desc: "Nombre, teléfono, correo o dirección", Icon: Contact },
  { id: "gasto", label: "Registrar un gasto", desc: "Guarda un ticket o factura y su importe", Icon: Receipt },
  { id: "ocio", label: "Entrada o reserva", desc: "Cine, teatro, restaurante, hotel, viaje…", Icon: Ticket },
];

const val = (a, k) => (a && a[k] && a[k].valor != null ? a[k].valor : "");
const lowconf = (a, k) =>
  a && a[k] && a[k].valor != null && a[k].valor !== "" && (a[k].confianza ?? 0) < CONF;

// Proyecta la próxima fecha de pago para obligaciones periódicas (IBI anual, comunidad mensual, etc.)
// a partir de una fecha base, avanzando el periodo hasta que caiga en el futuro.
const projectNextPayment = (base, period) => {
  if (!base || !period) return "";
  const p = String(period).toLowerCase();
  const months = /anual|año|ano/.test(p) ? 12
    : /semestr/.test(p) ? 6
    : /trimestr/.test(p) ? 3
    : /bimestr/.test(p) ? 2
    : /mensual|\bmes\b/.test(p) ? 1
    : 0;
  if (!months) return "";
  const d = new Date(base + "T00:00:00");
  if (isNaN(d.getTime())) return "";
  const today = new Date(`${hoyISO()}T00:00:00`);
  let guard = 0;
  while (d <= today && guard < 240) { d.setMonth(d.getMonth() + months); guard += 1; }
  return isoLocal(d);
};

export const AddModal = ({ open, onClose, initialFile = null, persona = null, opcion = null }) => {
  const { matters, familyMembers, pets, viviendas, vehiculos, subirDocumento, analizarDocumento, confirmarDocumento, eliminarDocumento, crearAsunto, createFamiliar, actualizarMascotaDesdeDocumento, createVivienda, actualizarViviendaDesdeDocumento, createVehiculo, detectarDuplicados, getDocumentoBlobUrl, marcarViviendaPendiente, recargarHogar, createMascota, gastos = [], guardarGasto, guardarCorreoRecibido } = useData();
  const [dupManual, setDupManual] = useState(null);
  const [otroSitio, setOtroSitio] = useState(false);
  const navigate = useNavigate();
  const [step, setStep] = useState("idle"); // idle | pregunta | modo | processing | review | manual | pausa-detectada
  const [pendiente, setPendiente] = useState(null);
  const [sinAnalisis, setSinAnalisis] = useState(false);
  const [cambioVenc, setCambioVenc] = useState(null);
  const [docId, setDocId] = useState(null);
  const [origName, setOrigName] = useState("");
  const [recurrente, setRecurrente] = useState(false);
  const [form, setForm] = useState(null);
  const [manual, setManual] = useState(null);
  const [verify, setVerify] = useState({});
  const [busy, setBusy] = useState(false);
  // Vinculación automática con mascotas (FASE 6)
  const [petMatch, setPetMatch] = useState(null);     // resultado de matchPets()
  const [linkedPet, setLinkedPet] = useState(null);   // mascota elegida para vincular
  const [linkDismissed, setLinkDismissed] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [petUpdates, setPetUpdates] = useState([]);   // cambios propuestos a la ficha
  const [petUpdated, setPetUpdated] = useState(false);
  const [updBusy, setUpdBusy] = useState(false);
  // Vinculación automática con viviendas (FASE 6 · Hogar)
  const [vivMatch, setVivMatch] = useState(null);
  const [linkedViv, setLinkedViv] = useState(null);
  const [vivDismissed, setVivDismissed] = useState(false);
  const [showVivPicker, setShowVivPicker] = useState(false);
  const [vivCreating, setVivCreating] = useState(false);
  const [vivUpdates, setVivUpdates] = useState([]);
  const [vivUpdated, setVivUpdated] = useState(false);
  const [vivUpdBusy, setVivUpdBusy] = useState(false);
  // Vinculación automática con vehículos (FASE 1 · Vehículos)
  const [vehMatch, setVehMatch] = useState(null);
  const [linkedVeh, setLinkedVeh] = useState(null);
  const [vehDismissed, setVehDismissed] = useState(false);
  const [vehCreating, setVehCreating] = useState(false);
  const fileRef = useRef(null);
  // Detección de duplicados al subir (FASE 7)
  const [dup, setDup] = useState(null);       // { duplicados: [], tiene_fuerte: bool }
  const [dupAck, setDupAck] = useState(false); // el usuario eligió "Guardar de todos modos"
  const [dupOpening, setDupOpening] = useState(null);
  // Ambigüedad de clasificación (FASE 9): análisis crudo + decisión vivienda/persona
  const [analisis, setAnalisis] = useState(null);
  const [dudaChoice, setDudaChoice] = useState(null); // null | 'vivienda' | 'persona' | 'pendiente'
  const [personaSug, setPersonaSug] = useState(null);
  const [vivSinAsignar, setVivSinAsignar] = useState(false);
  const [ocioDoc, setOcioDoc] = useState(null);
  const [petNueva, setPetNueva] = useState(null); // null | 'elegir' | 'no' | 'creando'
  const [gastoDec, setGastoDec] = useState({ modo: null });
  const [gastoDup, setGastoDup] = useState(null);
  const [esCorreo, setEsCorreo] = useState(false);
  const noClinico = useConfirmarNoClinico();
  const titularId = familyMembers.find((f) => f.es_titular)?.id || "fm-self";

  // Archivo que llega ya elegido (p. ej. «Hacer una foto» de la introducción).
  const onFileRef = useRef(null);
  const camara = useCamara((e) => onFileRef.current?.(e));
  useEffect(() => {
    if (open && persona) setManual((m) => ({ ...m, family_member_id: persona }));
  }, [open, persona]);
  // Abrir directamente una opción (p. ej. Agenda › «Añadir recordatorio»).
  useEffect(() => { if (open && opcion === "recordatorio") setStep("recordatorio"); }, [open, opcion]);
  useEffect(() => {
    if (open && initialFile && onFileRef.current) onFileRef.current({ target: { files: [initialFile], value: "" } });
  }, [open, initialFile]);
  if (!open) return null;

  const emptyManual = () => ({
    title: "", category: "Otros", family_member_id: persona || titularId,
    pet_id: "", provider: "", due_date: "", amount: "", notes: "", priority: "normal",
    due_time: "", tipo: "", reminder_date: "", reminder_time: "", reminder_text: "", aviso_offsets: [], avisos_activos: true,
  });

  const pick = (id) => {
    setSinAnalisis(false);
    if (id === "correo" || id === "recordatorio") { setEsCorreo(id === "correo"); setStep(id); return; }
    if (id === "gasto") {
      close();
      navigate("/gastos?registrar=1");
      return;
    }
    if (id === "ocio") { close(); navigate("/ocio?nuevo=1"); return; }
    if (id === "contacto") { close(); navigate("/contactos?nuevo=1"); return; }
    if (id === "manual") {
      setManual(emptyManual());
      setStep("manual");
      return;
    }
    if (id === "photo") camara.abrir(); else if (id === "galeria") camara.elegirGaleria(); else fileRef.current?.click();
  };

  const toggleOffset = (o) =>
    setManual((m) => ({
      ...m,
      aviso_offsets: m.aviso_offsets.includes(o)
        ? m.aviso_offsets.filter((x) => x !== o)
        : [...m.aviso_offsets, o],
    }));

  const guardarManual = async (forzar = false) => {
    if (!manual.title.trim()) return toast.error("Indica un título.");
    const payload = {
        title: manual.title.trim(),
        category: manual.category,
        family_member_id: manual.family_member_id || "fm-self",
        pet_id: manual.pet_id || null,
        provider: manual.provider,
        due_date: manual.due_date || "",
        due_time: manual.due_date ? manual.due_time || "" : "",
        tipo: manual.due_date ? manual.tipo || (manual.due_time ? "evento" : "obligacion") : null,
        amount: manual.amount === "" ? null : Number(manual.amount),
        notes: manual.notes,
        priority: manual.priority,
        reminder_date: manual.reminder_date || "",
        reminder_time: manual.reminder_date ? manual.reminder_time || "" : "",
        reminder_text: manual.reminder_text,
        aviso_offsets: avisosFuturos(manual.due_date, manual.aviso_offsets),
        avisos_activos: manual.avisos_activos,
      };
    const dup = forzar !== true && buscarDuplicado(matters, payload);
    if (dup) return setDupManual(dup);
    setDupManual(null);
    setBusy(true);
    try {
      const { asunto: creado } = await crearAsunto(payload);
      toast.success(mensajeGuardado(creado, pets, familyMembers), {
        description: manual.aviso_offsets.length ? "Avisos por email activados." : undefined, duration: 6000,
      });
      close();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo crear el asunto.");
    } finally {
      setBusy(false);
    }
  };

  const applyAnalysis = (a) => {
    setAnalisis(a);
    setDudaChoice(null);
    const related = val(a, "vehiculo_relacionado") || val(a, "inmueble_relacionado") || "";
    const period = val(a, "periodicidad");
    let due = val(a, "fecha_vencimiento") || "";
    let projected = false;
    // Si no hay vencimiento futuro pero es un pago periódico, proyectar el próximo pago.
    const esRecurrente = val(a, "recurrencia_explicita") === true;
    const esPoliza = /seguro|p[oó]liza|renovaci|suscrip|contrato|cuota/i.test(`${val(a, "tipo_documento")} ${val(a, "titulo_sugerido")}`);
    // Una factura o ticket ya pagado no genera «próximo pago» salvo póliza/suscripción con recurrencia explícita.
    if (!due && period && (val(a, "es_gasto") !== true || (esRecurrente && esPoliza))) {
      const next = projectNextPayment(val(a, "fecha_documento") || "", period);
      if (next) { due = next; projected = true; }
    }
    let rem = "";
    if (due && sumarDias(due, -8) >= hoyISO()) {
      rem = sumarDias(due, -8);
    }
    const category = CATEGORIES.includes(val(a, "categoria")) ? val(a, "categoria") : "Otros";
    const nombreDoc = nombrePropuesto({ analisis: a }) || "Documento";
    // Vencimiento claro y futuro (ITV, seguro, contrato, suscripción…): asunto con avisos previos.
    const vence = !projected && !!due && due > hoyISO() && (val(a, "es_gasto") !== true || esPoliza || esRecurrente);
    const esITV = /\bitv\b|inspecci[oó]n t[eé]cnica/i.test(`${val(a, "tipo_documento")} ${val(a, "titulo_sugerido")} ${a?.vehiculo?.tipo_doc_vehiculo?.valor || ""}`);
    setForm({
      title: vence && esITV ? "Próxima ITV" : nombreDoc,
      doc_title: nombreDoc,
      aviso_offsets: vence ? avisosFuturos(due, [30, 7, 1]) : [],
      avisos_activos: vence,
      category,
      matter_type: val(a, "tipo_documento"),
      provider: val(a, "proveedor"),
      family_member_id: persona || titularId,
      pet_id: "",
      related_to: related,
      due_date: due,
      amount: a && a.importe && a.importe.valor != null ? dec2(a.importe.valor) : "",
      previous_amount: a && a.importe_anterior && a.importe_anterior.valor != null ? dec2(a.importe_anterior.valor) : "",
      periodicity: period,
      auto_renew: a && a.renovacion_automatica ? a.renovacion_automatica.valor === true : false,
      relevant_info: val(a, "informacion_relevante"),
      next_action: val(a, "proxima_accion"),
      reminder_date: vence ? "" : rem,
      reminder_time: "",
      reminder_text: projected ? `Próximo pago de ${val(a, "titulo_sugerido") || "este asunto"}` : vence && esITV ? "Pedir cita para la próxima ITV" : "",
    });
    setVerify({
      provider: lowconf(a, "proveedor"),
      due_date: lowconf(a, "fecha_vencimiento"),
      amount: lowconf(a, "importe"),
      periodicidad: lowconf(a, "periodicidad"),
      renovacion_automatica: lowconf(a, "renovacion_automatica"),
    });
    // Cotejar con mascotas existentes (microchip / pasaporte / nombre).
    const sug = sugerirPersona(familyMembers, [val(a, "persona_relacionada"), a?.vehiculo?.titular?.valor, a?.mascota?.propietario?.valor].filter(Boolean));
    setPersonaSug(sug && sug.id !== (persona || titularId) ? sug : null);
    setPetMatch(matchPets(a, pets));
    setLinkedPet(null);
    setLinkDismissed(false);
    setShowPicker(false);
    setPetUpdates([]);
    setPetUpdated(false);
    // Cotejar con viviendas existentes (por dirección de suministro/servicio).
    setVivMatch(matchViviendas(a, viviendas));
    setVehMatch(matchVehiculos(a, vehiculos));
    setLinkedViv(null);
    setVivDismissed(false);
    setShowVivPicker(false);
    setVivUpdates([]);
    setVivUpdated(false);
  };

  // --- Viviendas: vincular el documento (en revisión) a una vivienda y calcular datos nuevos.
  const linkToViv = (viv) => {
    setLinkedViv(viv);
    setShowVivPicker(false);
    setForm((f) => ({ ...f, vivienda_id: viv.id, category: "Hogar" }));
    setVivUpdates(vivMatch ? computeViviendaUpdates(vivMatch.data, viv) : []);
    setVivUpdated(false);
  };

  const crearVivDesdeDoc = async () => {
    const d = vivMatch?.data || {};
    setVivCreating(true);
    try {
      const alias = d.municipio ? `Vivienda ${d.municipio}` : "Vivienda nueva";
      const nueva = await createVivienda({
        alias, direccion: d.primaryAddress || "", municipio: d.municipio || "",
        provincia: d.provincia || "", codigo_postal: d.codigo_postal || "",
        referencia_catastral: d.referencia_catastral || "",
        aseguradora_hogar: d.aseguradora || "", numero_poliza: d.numero_poliza || "",
        tipo_tenencia: "", tipo_inmueble: "", uso: "", titulares: "", fecha_adquisicion: "", notas: "", icon: "Home",
      });
      toast.success(`Vivienda «${nueva.alias}» creada`);
      // recién creada: sin conflictos, no hace falta proponer actualización.
      setLinkedViv(nueva);
      setForm((f) => ({ ...f, vivienda_id: nueva.id, category: "Hogar" }));
      setVivUpdates([]);
      setVivUpdated(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo crear la vivienda.");
    } finally {
      setVivCreating(false);
    }
  };

  // --- Vehículos: vincular / crear desde el documento (en revisión).
  const linkToVeh = (veh) => {
    setLinkedVeh(veh);
    setForm((f) => ({ ...f, vehiculo_id: veh.id, category: "Vehículos" }));
  };
  const crearVehDesdeDoc = async () => {
    const d = vehMatch?.data || {};
    setVehCreating(true);
    try {
      const alias = d.marca && d.modelo ? `${d.marca} ${d.modelo}` : (d.matricula || "Vehículo nuevo");
      const nuevo = await createVehiculo({
        alias, matricula: d.matricula || "", marca: d.marca || "", modelo: d.modelo || "",
        vin: d.vin || "", titular: d.titular || "", anio: "", notas: "", icon: "Car",
      });
      toast.success(`Vehículo «${nuevo.alias}» creado`);
      setLinkedVeh(nuevo);
      setForm((f) => ({ ...f, vehiculo_id: nuevo.id, category: "Vehículos" }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo crear el vehículo.");
    } finally {
      setVehCreating(false);
    }
  };


  const applyVivUpdates = async (updates) => {
    setVivUpdBusy(true);
    try {
      await actualizarViviendaDesdeDocumento(linkedViv.id, {
        doc_id: docId, updates,
        nota_historial: `Ficha actualizada desde «${form?.title || "documento"}».`,
      });
      setVivUpdated(true);
      toast.success(`Ficha de ${linkedViv.alias} actualizada`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo actualizar la ficha.");
    } finally {
      setVivUpdBusy(false);
    }
  };

  // Vincula el documento (en revisión) a una mascota y calcula los datos nuevos/conflictivos.
  const linkToPet = (pet) => {
    setLinkedPet(pet);
    setShowPicker(false);
    setForm((f) => ({ ...f, pet_id: pet.id, category: "Mascotas" }));
    setPetUpdates(petMatch ? computePetUpdates(petMatch.data, pet) : []);
    setPetUpdated(false);
  };

  // Crea la mascota solo con los datos que figuran en el documento y la vincula.
  const registrarMascota = async () => {
    const d = petMatch.data;
    setPetNueva("creando");
    try {
      const pet = await createMascota({ name: d.nombre, species: d.especie || "", breed: d.raza || "", sex: d.sexo || "",
        birth_date: d.fecha_nacimiento || "", microchip: d.microchip || "", vet: d.veterinario || "", owner_member_id: titularId });
      setPetMatch({ ...petMatch, candidates: [] });
      setLinkedPet(pet); setShowPicker(false); setPetUpdates([]); setPetUpdated(false);
      setForm((f) => ({ ...f, pet_id: pet.id }));
      toast.success(`${pet.name} registrado`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo registrar la mascota.");
      setPetNueva(null);
    }
  };

  const applyPetUpdates = async (updates) => {
    setUpdBusy(true);
    try {
      await actualizarMascotaDesdeDocumento(linkedPet.id, {
        doc_id: docId,
        updates,
        nota_historial: `Ficha actualizada desde «${form?.title || "documento"}».`,
      });
      setPetUpdated(true);
      toast.success(`Ficha de ${linkedPet.name} actualizada`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo actualizar la ficha.");
    } finally {
      setUpdBusy(false);
    }
  };

  const onFile = async (e) => {
    const elegido = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!elegido) return;
    const file = await prepararArchivo(elegido);
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["pdf", "jpg", "jpeg", "png"].includes(ext)) {
      return toast.error("Formato no admitido. Usa PDF, JPG o PNG.");
    }
    if (file.size > 10 * 1024 * 1024) {
      return toast.error("El archivo supera el límite de 10 MB.");
    }
    setPendiente(file);
    setStep("modo");
  };

  const guardarSinIA = async (nombre) => {
    setBusy(true);
    try {
      await subirDocumento(pendiente, { sin_ia: "si", confirma_no_clinico: "si", nombre });
      toast.success("Guardado en Documentos sin análisis con IA");
      close();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar el documento.");
      setBusy(false);
    }
  };

  const procesar = async (file, subir = null) => {
    setStep("processing");
    setOrigName(file.name);
    try {
      const doc = subir ? await subir() : await subirDocumento(file);
      if (doc.pausa_salud) { setDocId(null); return setStep("no-admitido"); }
      setDocId(doc.id);
      const res = await analizarDocumento(doc.id);
      if (res.pausa_salud) { setDocId(null); return setStep("no-admitido"); }
      const analisis = res.analisis;
      // Entrada, reserva o billete: se lleva al flujo de Ocio y Reservas (con su persona y sus recordatorios).
      if (!subir && val(analisis, "categoria") === "Ocio" && (analisis.categoria.confianza || 0) >= 0.55) return setOcioDoc(doc);
      applyAnalysis(analisis);
      try {
        const d = await detectarDuplicados(doc.id);
        setDup(d);
        setDupAck(!d.tiene_fuerte);
      } catch { setDup(null); setDupAck(true); }
      setStep("review");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo procesar el documento.");
      setStep(subir ? "correo" : "idle");
    }
  };

  onFileRef.current = onFile;

  const enviarCorreo = (fd, sinIA) => {
    if (!sinIA) return procesar({ name: fd.get("asunto") || "Correo" }, () => guardarCorreoRecibido(fd));
    noClinico.pedir(async () => {
      setBusy(true);
      try {
        await guardarCorreoRecibido(fd);
        toast.success("Correo guardado en Documentos sin análisis con IA");
        close();
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar el correo.");
        setBusy(false);
      }
    }, fd.get("file") ? "este correo y su adjunto" : "este correo");
  };

  const verDuplicado = async (d) => {
    setDupOpening(d.id);
    try {
      abrirVisor(d);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo abrir el documento.");
    } finally { setDupOpening(null); }
  };

  const confirmar = async (extra = {}, gd = gastoDec) => {
    if (!form.title.trim()) return toast.error("Indica un título para el asunto.");
    if (pideGasto && !gd.modo) return toast.error("Elige si quieres registrar también el gasto.");
    setBusy(true);
    setCambioVenc(null);
    try {
      if (gd.modo === "si" && !gd.forzar) {
        const { data } = await api.post("/gastos/duplicados", { ...gd.datos, documento_id: docId });
        if (data.duplicados.length) { setGastoDup(data.duplicados); setBusy(false); return; }
      }
      const verify_fields = Object.keys(verify).filter((k) => verify[k]);
      const r = await confirmarDocumento(docId, {
        title: form.title.trim(),
        // «Guardar sin asociar» una mascota no registrada: se guarda sin mascota (no queda pendiente de clasificar).
        category: form.category === "Mascotas" && !form.pet_id && petNueva === "no" ? "Otros" : form.category,
        matter_type: form.matter_type,
        provider: form.provider,
        family_member_id: form.family_member_id || "fm-self",
        pet_id: form.pet_id || null,
        vivienda_id: form.vivienda_id || null,
        vehiculo_id: form.vehiculo_id || null,
        related_to: form.related_to,
        summary: form.relevant_info,
        due_date: form.due_date || "",
        amount: parseImporte(form.amount),
        previous_amount: parseImporte(form.previous_amount),
        periodicity: form.periodicity,
        auto_renew: !!form.auto_renew,
        relevant_info: form.relevant_info,
        next_action: form.next_action,
        verify_fields,
        reminder_date: form.reminder_date || "",
        reminder_time: form.reminder_date ? form.reminder_time || "" : "",
        reminder_text: form.reminder_text,
        doc_title: form.doc_title || "",
        aviso_offsets: form.due_date ? avisosFuturos(form.due_date, form.aviso_offsets || []) : [],
        avisos_activos: !!form.due_date && avisosFuturos(form.due_date, form.aviso_offsets || []).length > 0,
        vivienda_pendiente: servicioHogar,
        vivienda_sin_asignar: vivSinAsignar && !form.vivienda_id,
        ya_pagado: gd.modo === "si",
        ...extra,
      });
      let gastoTxt = "";
      if (gd.modo === "si") {
        const g = { ...gd.datos, documento_id: docId };
        // Asociaciones confirmadas en el documento después de preparar el gasto.
        const [t, id] = asociadoGasto.split(":");
        if (asociadoGasto && asociadoGasto !== gd.ini?.asociado && !g.pet_id && !g.vehiculo_id && !g.vivienda_id) g[{ mascota: "pet_id", vehiculo: "vehiculo_id", vivienda: "vivienda_id" }[t]] = id;
        if (personaGasto && personaGasto !== gd.ini?.persona && !g.family_member_id) g.family_member_id = personaGasto;
        try { await guardarGasto(null, g); gastoTxt = `Gasto registrado: ${eur(gd.datos.importe)}`; }
        catch { toast.error("El documento se ha guardado, pero no se pudo registrar el gasto."); }
      }
      const avisos = !r?.existente && (form.reminder_date || (form.due_date && avisosFuturos(form.due_date, form.aviso_offsets || []).length)) ? "Avisos programados" : "";
      if (r?.existente) toast.success(r.actualizado ? "Fecha actualizada" : "Documento añadido al asunto que ya tenías", { description: [r.actualizado ? "He cambiado la fecha y los avisos." : "No he creado avisos repetidos.", gastoTxt].filter(Boolean).join(" · ") });
      else toast.success("Documento guardado", { description: [gastoTxt, r?.asunto?.status === "HECHO" ? "Pago ya realizado: guardado como hecho" : avisos].filter(Boolean).join(" · ") || undefined });
      close();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.code === "VENCIMIENTO_CAMBIA") { setCambioVenc(d); return; }
      toast.error(formatApiErrorDetail(d) || "No se pudo confirmar.");
    } finally {
      setBusy(false);
    }
  };

  const rechazar = async () => {
    if (docId) {
      try { await eliminarDocumento(docId); } catch { /* noop */ }
    }
    toast.info("Documento descartado");
    close();
  };

  const close = () => {
    setStep("idle"); setDocId(null); setForm(null); setManual(null); setVerify({}); setBusy(false); setSinAnalisis(false);
    setPetMatch(null); setLinkedPet(null); setLinkDismissed(false); setShowPicker(false);
    setPetUpdates([]); setPetUpdated(false); setUpdBusy(false);
    setVivMatch(null); setLinkedViv(null); setVivDismissed(false); setShowVivPicker(false);
    setVivCreating(false); setVivUpdates([]); setVivUpdated(false); setVivUpdBusy(false);
    setVehMatch(null); setLinkedVeh(null); setVehDismissed(false); setVehCreating(false);
    setDup(null); setDupAck(false); setDupOpening(null);
    setAnalisis(null); setDudaChoice(null); setRecurrente(false); setPendiente(null); setVivSinAsignar(false); setOcioDoc(null); setPetNueva(null);
    setGastoDec({ modo: null }); setGastoDup(null); setEsCorreo(false); setOtroSitio(false);
    onClose();
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // --- Ambigüedad de clasificación (FASE 9) ---
  const catConf = analisis && analisis.categoria ? (analisis.categoria.confianza || 0) : 0;
  const hasVivAddr = !!(vivMatch && (vivMatch.data?.primaryAddress || vivMatch.data?.referencia_catastral));
  const cat = form?.category;
  // Duda de área: la IA tiene poca seguridad, lo deja en «Otros» o hay señal de vivienda en un doc personal.
  const dudaVivPersona = !!analisis && !linkedViv && !vivDismissed && dudaChoice === null && petNueva !== "no" &&
    (cat === "Otros" || catConf < 0.55 || (cat === "Contratos y seguros" && hasVivAddr));
  // Varias viviendas posibles: 2+ candidatas y ninguna coincidencia clara (confianza alta).
  const variasViviendas = !!vivMatch && !linkedViv && !vivDismissed && !dudaVivPersona &&
    dudaChoice !== "persona" && dudaChoice !== "pendiente" &&
    vivMatch.candidates.length >= 2 && (!vivMatch.best || vivMatch.best.confidence !== "alta");
  const elegirVivienda = (v) => linkToViv(v);
  // Servicio doméstico o de mantenimiento del hogar sin dirección reconocible: preguntar por la vivienda.
  const textoDoc = analisis ? ["tipo_documento", "titulo_sugerido", "informacion_relevante"].map((k) => analisis[k]?.valor || "").join(" ") : "";
  const servicioHogar = !!analisis && viviendas.length > 0 && !linkedViv && !vivDismissed && !dudaVivPersona && !variasViviendas && petNueva !== "no" &&
    dudaChoice !== "persona" && dudaChoice !== "pendiente" && !(vivMatch && (vivMatch.candidates.length > 0 || vivMatch.data.primaryAddress)) &&
    !linkedVeh && !linkedPet && !(vehMatch?.candidates?.length) && !hasVehiculoData(analisis) && cat !== "Vehículos" && cat !== "Mascotas" &&
    (cat === "Hogar" || /dom[eé]stic|del hogar|de la vivienda|limpieza|jardiner|fontaner|electricista|calefacci|caldera|aire acondicionado|piscina|cerrajer/i.test(textoDoc));
  const avisosSel = form?.due_date ? avisosFuturos(form.due_date, form.aviso_offsets || []).sort((x, y) => y - x) : [];
  const conAvisosVenc = avisosSel.length > 0 && !form.reminder_date;
  // Gasto: se pregunta si la IA lo ve como gasto o si es factura/recibo/ticket con importe total.
  const gastoYa = !!docId && gastos.some((g) => g.documento_id === docId);
  const docPago = /factura|recibo|ticket|bizum|transferencia|justificante/i.test(`${val(analisis, "tipo_documento")} ${val(analisis, "titulo_sugerido")}`);
  const pideGasto = !!analisis && !gastoYa && Number(val(analisis, "importe")) > 0 && (analisis.es_gasto?.valor === true || docPago);
  const sugGasto = analisis ? sugerirPersona(familyMembers, [val(analisis, "persona_relacionada"), analisis?.vehiculo?.titular?.valor, analisis?.mascota?.propietario?.valor].filter(Boolean)) : null;
  const personaGasto = persona || (form && form.family_member_id && form.family_member_id !== titularId ? form.family_member_id : sugGasto?.id === titularId ? titularId : "");
  const asociadoGasto = linkedPet ? `mascota:${linkedPet.id}` : linkedVeh ? `vehiculo:${linkedVeh.id}` : linkedViv ? `vivienda:${linkedViv.id}` : "";
  // PM-9: si la IA ve un contacto, solo Contactos o Pendientes; lo demás tras «Guardarlo en otro sitio».
  const soloContacto = step === "review" && esContacto(analisis) && !otroSitio;
  const dupFuerte = !!(dup && dup.tiene_fuerte && !dupAck);
  // Cerrar sin responder a la pregunta de vivienda: queda pendiente para resolverla después.
  const cerrar = () => {
    if (step === "review" && servicioHogar && docId) marcarViviendaPendiente(docId).catch(() => {});
    else if (step === "review" && docId) recargarHogar().catch(() => {});
    if (step === "review" && docId) toast.info("El documento queda en Pendientes de clasificar.");
    close();
  };

  if (ocioDoc) return <OcioAdd docInicial={ocioDoc} onClose={close} />;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="add-modal">
      <div className="absolute inset-0 bg-[#1C2421]/40 backdrop-blur-sm" onClick={step === "no-admitido" ? rechazar : cerrar} />
      <div className="relative w-full sm:max-w-lg bg-[#FAF8F5] rounded-t-3xl sm:rounded-3xl p-6 pb-8 shadow-2xl aldia-fade-up max-h-[90vh] overflow-y-auto">
        <button
          data-testid="add-modal-close"
          onClick={step === "no-admitido" ? rechazar : cerrar}
          aria-label="Cerrar"
          className="absolute top-4 right-4 w-11 h-11 rounded-full bg-[#F2EDE4] flex items-center justify-center hover:bg-[#E5DFD5] transition-colors"
        >
          <X size={22} className="text-[#4A5550]" />
        </button>

        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/*"
          onChange={onFile} className="hidden" data-testid="add-file-input" />
        {camara.inputs}

        {step === "idle" && (
          <>
            <h2 className="font-heading text-2xl font-bold text-[#1C2421]">¿Qué quieres añadir?</h2>
            {persona && <p data-testid="add-persona-fijada" className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#E8F0EC] text-[#2E5645] text-base font-semibold">Para <PersonaChip id={persona} size="sm" /></p>}
            <p className="text-base text-[#6E7A74] mt-1 mb-5">Elige cómo entregar la información a tu agente.</p>
            <div className="space-y-3">
              {OPTIONS.filter((o) => !o.soloMovil || camara.esMovil).map(({ id, label, desc, Icon }) => (
                <button
                  key={id}
                  data-testid={`add-option-${id}`}
                  onClick={() => pick(id)}
                  className="w-full flex items-center gap-4 bg-white border border-[#E5DFD5] rounded-2xl p-4 text-left hover:border-[#3A6B56] hover:shadow-sm transition-all min-h-[72px]"
                >
                  <div className="shrink-0 w-12 h-12 rounded-xl bg-[#E8F0EC] flex items-center justify-center">
                    <Icon size={24} className="text-[#3A6B56]" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-lg text-[#1C2421]">{label}</p>
                    <p className="text-sm text-[#6E7A74]">{desc}</p>
                  </div>
                </button>
              ))}
            </div>
            <p className="text-sm text-[#6E7A74] mt-4" data-testid="add-transparencia-ia">AL DÍA analizará el documento para organizarlo automáticamente.</p>
            <button data-testid="add-guardar-sin-analisis" onClick={() => noClinico.pedir(() => { pick("upload"); setSinAnalisis(true); })}
              className="mt-1 min-h-[44px] text-sm font-semibold text-[#4A5550] underline underline-offset-4">Guardar sin análisis</button>
            {camara.aviso}
            {noClinico.dialogo}
            <div className="mt-5 bg-[#F6F3EE] rounded-xl p-4 flex gap-3" data-testid="add-privacy-notice">
              <ShieldAlert size={18} className="text-[#3A6B56] shrink-0 mt-0.5" />
              <p className="text-sm text-[#6E7A74] leading-relaxed">
                {IA_DOCS_PAUSADA
                  ? "Tus documentos son privados y solo tú puedes verlos. El análisis automático con IA está temporalmente suspendido: tus documentos se guardan en AL DÍA sin enviarse a ningún servicio de IA."
                  : "Tus documentos son privados y solo tú puedes verlos. Para organizarlos, AL DÍA los envía de forma segura a un servicio de análisis de IA. Si eliges «Guardar sin análisis», no se envía a ningún servicio de IA. No subas informes médicos, recetas ni documentación clínica."}
              </p>
            </div>
          </>
        )}

        {esCorreo && ["correo", "processing"].includes(step) && (
          <div className={step === "correo" ? "" : "hidden"}><CorreoForm ocupado={busy} onEnviar={enviarCorreo} onCancelar={close} />{noClinico.dialogo}</div>
        )}
        {step === "recordatorio" && <RecordatorioPuntual onDone={close} onCancel={close} />}
        {step === "modo" && pendiente && (<>
          <ElegirAnalisis soloSinIA={sinAnalisis} pedirNoClinico={noClinico.pedir} archivo={pendiente.name} ocupado={busy} onAnalizar={() => procesar(pendiente)} onSinIA={guardarSinIA} onCancelar={close} />
          {noClinico.dialogo}
        </>)}
        {step === "no-admitido" && (
          <div data-testid="add-no-admitido">
            <h2 className="font-heading text-2xl font-bold text-[#1C2421] pr-12">Este documento no se puede guardar</h2>
            <p className="text-base text-[#4A5550] mt-3">Parece un informe médico, una receta o documentación clínica. AL DÍA no admite este tipo de documentos en esta fase, así que no se ha guardado.</p>
            <button data-testid="add-no-admitido-cerrar" onClick={close} className="w-full mt-5 min-h-[52px] rounded-xl bg-[#3A6B56] text-white font-semibold text-lg">Entendido</button>
          </div>
        )}
        {step === "processing" && (
          <div className="py-12 text-center" data-testid="add-processing">
            <div className="w-16 h-16 mx-auto rounded-full border-4 border-[#E5DFD5] border-t-[#3A6B56] animate-spin" />
            <p className="font-heading text-xl font-bold text-[#1C2421] mt-6">Analizando el documento…</p>
            <p className="text-base text-[#6E7A74] mt-1">AL DÍA está leyendo y entendiendo la información.</p>
            <p className="text-sm text-[#6E7A74] mt-3 max-w-xs mx-auto" data-testid="add-aviso-ia">Lo lee Gemini, de Google, a través de Emergent. Más información en Perfil › Privacidad y seguridad.</p>
          </div>
        )}

        {step === "manual" && manual && (
          <div data-testid="manual-form">
            <h2 className="font-heading text-2xl font-bold text-[#1C2421]">Introducir manualmente</h2>
            <div className="grid grid-cols-2 gap-1 bg-[#F2EDE4] rounded-2xl p-1.5 mt-3" data-testid="manual-tipo">
              {[[false, "Asunto puntual"], [true, "Pago o recordatorio periódico"]].map(([v, l]) => (
                <button key={l} data-testid={`manual-tipo-${v ? "recurrente" : "puntual"}`} onClick={() => setRecurrente(v)}
                  className={`min-h-[48px] rounded-xl font-semibold text-base ${recurrente === v ? "bg-white text-[#1C2421] shadow-sm" : "text-[#6E7A74]"}`}>{l}</button>
              ))}
            </div>
            <p className="text-base text-[#6E7A74] mt-3 mb-4">{recurrente ? "Para pagos, cuotas o avisos que se repiten en el tiempo." : "Crea un asunto sin subir ningún documento."}</p>
            {recurrente ? (
              <>
                <div className="space-y-3" data-testid="manual-recurrente">
                  <p className="text-base text-[#4A5550]">Te guiaremos en tres pasos sencillos.</p>
                  <button data-testid="manual-recurrente-empezar" onClick={() => { close(); navigate("/gestiones?nuevo=1"); }}
                    className="w-full min-h-[56px] rounded-2xl bg-[#3A6B56] text-white font-bold text-lg">Crear recordatorio periódico</button>
                </div>
                <button data-testid="gestion-volver" onClick={() => { setStep("idle"); setManual(null); setRecurrente(false); }}
                  className="mt-3 w-full min-h-[52px] rounded-xl bg-[#F2EDE4] text-[#1C2421] font-semibold text-base border border-[#E5DFD5]">Volver</button>
              </>
            ) : (<>
            <div className="space-y-3.5">
              <TextField label="Título" testid="manual-title" value={manual.title}
                onChange={(v) => setManual({ ...manual, title: v })} placeholder="Ej. Renovar el DNI" />
              <div>
                <Label>Categoría</Label>
                <select data-testid="manual-category" value={manual.category}
                  onChange={(e) => setManual({ ...manual, category: e.target.value })} className={inputCls}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
                </select>
              </div>
              <PersonSelect testid="manual-family" value={manual.family_member_id}
                onChange={(v) => setManual({ ...manual, family_member_id: v })}
                familyMembers={familyMembers} createFamiliar={createFamiliar} />
              {manual.category === "Mascotas" && pets.length > 0 && (
                <div>
                  <Label>Mascota relacionada</Label>
                  <select data-testid="manual-pet" value={manual.pet_id}
                    onChange={(e) => setManual({ ...manual, pet_id: e.target.value })} className={inputCls}>
                    <option value="">Ninguna</option>
                    {pets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              )}
              <TextField label="Proveedor / entidad (opcional)" testid="manual-provider" value={manual.provider}
                onChange={(v) => setManual({ ...manual, provider: v })} />
              <div className="grid grid-cols-2 gap-3 items-end">
                <TextField label="Fecha de la cita / vencimiento" type="date" testid="manual-due"
                  value={manual.due_date} onChange={(v) => setManual({ ...manual, due_date: v })} />
                <TextField label="Hora (opcional)" type="time" testid="manual-due-time"
                  value={manual.due_time} onChange={(v) => setManual({ ...manual, due_time: v })} />
              </div>
              {manual.due_date && (
                <div>
                  <Label>¿Qué es?</Label>
                  <div className="flex gap-2" data-testid="manual-tipo">
                    {[["evento", "Cita o evento"], ["obligacion", "Pago, renovación o trámite"]].map(([v, l]) => {
                      const sel = (manual.tipo || (manual.due_time ? "evento" : "obligacion")) === v;
                      return (
                        <button key={v} type="button" data-testid={`manual-tipo-${v}`} onClick={() => setManual({ ...manual, tipo: v })}
                          className={`flex-1 min-h-[48px] px-2 rounded-xl font-semibold text-sm border ${sel ? "bg-[#3A6B56] text-white border-[#3A6B56]" : "bg-white text-[#4A5550] border-[#E5DFD5]"}`}>{l}</button>
                      );
                    })}
                  </div>
                </div>
              )}
              <TextField label="Importe (€, opcional)" type="number" testid="manual-amount"
                value={manual.amount} onChange={(v) => setManual({ ...manual, amount: v })} />
              <TextField label="Notas (opcional)" testid="manual-notes" value={manual.notes}
                onChange={(v) => setManual({ ...manual, notes: v })} />
              <div>
                <Label>Prioridad</Label>
                <div className="flex gap-2" data-testid="manual-priority">
                  {PRIORIDADES.map((p) => (
                    <button key={p.id} type="button" data-testid={`manual-priority-${p.id}`}
                      onClick={() => setManual({ ...manual, priority: p.id })}
                      className={`flex-1 min-h-[48px] rounded-xl font-semibold text-base border transition-colors ${
                        manual.priority === p.id
                          ? "bg-[#3A6B56] text-white border-[#3A6B56]"
                          : "bg-white text-[#4A5550] border-[#E5DFD5] hover:border-[#3A6B56]"
                      }`}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-[#E8F0EC] rounded-2xl p-4 space-y-3">
                <p className="font-heading font-bold text-base text-[#1C2421]">Recordatorio (opcional)</p>
                <TextField label="Avísame el" type="date" testid="manual-reminder-date" borrable
                  value={manual.reminder_date} onChange={(v) => setManual({ ...manual, reminder_date: v })} />
                <HoraAviso value={manual.reminder_time} onChange={(t) => setManual({ ...manual, reminder_time: t })} testid="manual-reminder-hora" />
                <TextField label="Texto del aviso" testid="manual-reminder-text"
                  value={manual.reminder_text} onChange={(v) => setManual({ ...manual, reminder_text: v })}
                  placeholder="Ej. Pedir cita" />
              </div>

              <div className="bg-[#F6F3EE] rounded-2xl p-4">
                <p className="font-heading font-bold text-base text-[#1C2421] mb-1">Avisos por email antes del vencimiento</p>
                <p className="text-sm text-[#6E7A74] mb-3">Elige cuándo quieres recibir el aviso (requiere fecha de vencimiento).</p>
                <div className="flex flex-wrap gap-2" data-testid="manual-avisos">
                  {avisosFuturos(manual.due_date, AVISO_OPTS).map((o) => (
                    <button key={o} type="button" data-testid={`manual-aviso-${o}`}
                      onClick={() => toggleOffset(o)}
                      className={`min-h-[44px] px-4 rounded-full font-semibold text-sm border transition-colors ${
                        manual.aviso_offsets.includes(o)
                          ? "bg-[#3A6B56] text-white border-[#3A6B56]"
                          : "bg-white text-[#4A5550] border-[#E5DFD5] hover:border-[#3A6B56]"
                      }`}>
                      {o === 1 ? "1 día antes" : `${o} días antes`}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {dupManual && (
              <div data-testid="manual-duplicado" className="mt-5 bg-[#FFFBEB] border border-[#FDE68A] rounded-2xl p-4 space-y-3">
                <p className="text-base text-[#92400E]">Ya tienes <strong>{dupManual.title}</strong>{cuandoTxt(dupManual) ? ` el ${cuandoTxt(dupManual)}` : ""}. ¿Guardar igualmente?</p>
                <div className="flex gap-2">
                  <button data-testid="manual-duplicado-cancelar" onClick={() => setDupManual(null)} className="flex-1 min-h-[48px] rounded-xl bg-white border border-[#E5DFD5] font-semibold text-[#4A5550]">Cancelar</button>
                  <button data-testid="manual-duplicado-guardar" onClick={() => guardarManual(true)} disabled={busy} className="flex-1 min-h-[48px] rounded-xl bg-[#B45309] text-white font-semibold">Guardar igualmente</button>
                </div>
              </div>
            )}
            <div className="mt-5 flex gap-2.5">
              <button data-testid="manual-cancel" onClick={() => { setStep("idle"); setManual(null); }}
                className="flex-1 min-h-[52px] rounded-xl bg-[#F2EDE4] text-[#1C2421] font-semibold text-base border border-[#E5DFD5]">
                Volver
              </button>
              <button data-testid="manual-save" onClick={() => guardarManual()} disabled={busy || !!dupManual}
                className="flex-[2] min-h-[56px] rounded-xl bg-[#3A6B56] text-white font-semibold text-lg hover:bg-[#2E5645] transition-colors disabled:opacity-60">
                {busy ? "Guardando…" : "Crear asunto"}
              </button>
            </div>
            </>)}
          </div>
        )}

        {step === "review" && form && (
          <div data-testid="add-review">
            <div className="flex items-center gap-2 text-[#15803D]">
              <FileCheck2 size={24} />
              <h2 className="font-heading text-2xl font-bold text-[#1C2421]">He entendido este documento</h2>
            </div>
            <p className="text-base text-[#6E7A74] mt-1">
              El documento ya está guardado en Pendientes. El asunto y el gasto se crean al confirmar.
            </p>
            {gastoYa && <p data-testid="sugerencia-gasto-hecho" className="mt-4 bg-[#E8F0EC] text-[#2E5645] rounded-2xl p-3 font-semibold w-full">Este documento ya está registrado en Gastos.</p>}
            {soloContacto && dupFuerte
              ? <ContactoYaGuardado duplicados={dup.duplicados.filter((c) => c.strength === "fuerte")} analisis={analisis} docId={docId}
                  onGuardarIgual={() => setDupAck(true)} onCancelar={rechazar} onTerminar={close} />
              : esContacto(analisis) && <ContactoDetectado analisis={analisis} docId={docId} onTerminar={close}
                  acciones={soloContacto ? <OpcionesContacto onPendiente={cerrar} onOtroSitio={() => setOtroSitio(true)} /> : null} />}
            {!soloContacto && (<>
            {pideGasto && !(dup && dup.tiene_fuerte && !dupAck) && (
              <SugerenciaGasto docId={docId} analisis={analisis} asociado={asociadoGasto} persona={personaGasto}
                decision={gastoDec} setDecision={(d) => { setGastoDec(d); setGastoDup(null); }} />
            )}

            {dup && dup.tiene_fuerte && !dupAck ? (
              <div className="mt-4 bg-[#FFF7ED] border border-[#FED7AA] rounded-2xl p-4" data-testid="dup-strong-card">
                <div className="flex items-center gap-2 text-[#9A3412]">
                  <Files size={20} />
                  <h3 className="font-heading font-bold text-base text-[#1C2421]">
                    Este documento parece muy parecido a otro que ya tienes guardado
                  </h3>
                </div>
                <div className="mt-3 space-y-2">
                  {dup.duplicados.filter((c) => c.strength === "fuerte").map((c) => (
                    <div key={c.id} className="bg-white border border-[#E5DFD5] rounded-xl p-3" data-testid={`dup-item-${c.id}`}>
                      <p className="font-semibold text-base text-[#1C2421] truncate">{c.filename}</p>
                      <p className="text-sm text-[#6E7A74]">{catLabel(c.category)}{c.date_label ? ` · ${c.date_label}` : ""}</p>
                      <p className="text-sm text-[#9A3412] mt-1">Coincide en: <span className="font-semibold">{reasonsText(c.reasons)}</span></p>
                      <button data-testid={`dup-view-${c.id}`} onClick={() => verDuplicado(c)} disabled={dupOpening === c.id}
                        className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2E5645] disabled:opacity-60">
                        {dupOpening === c.id ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />} Ver documento existente
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col gap-2 mt-4">
                  <button data-testid="dup-save-anyway" onClick={() => setDupAck(true)}
                    className="w-full min-h-[48px] rounded-xl bg-[#3A6B56] text-white font-semibold text-base hover:bg-[#2E5645]">
                    Guardar de todos modos
                  </button>
                  <button data-testid="dup-cancel" onClick={rechazar}
                    className="w-full min-h-[44px] rounded-xl bg-white text-[#B91C1C] font-semibold text-sm border border-[#E5DFD5] hover:bg-[#FEF2F2]">
                    Cancelar y descartar
                  </button>
                </div>
              </div>
            ) : (
            <>
            {dup && !dup.tiene_fuerte && dup.duplicados.length > 0 && (
              <div className="mt-4 bg-[#F6F3EE] border border-[#E5DFD5] rounded-xl p-3.5 flex gap-3" data-testid="dup-partial-note">
                <Info size={18} className="text-[#B45309] shrink-0 mt-0.5" />
                <p className="text-sm text-[#6E7A74] leading-relaxed">
                  Se parece en algún dato a «{dup.duplicados[0].filename}» ({reasonsText(dup.duplicados[0].reasons)}), pero no es concluyente. Puedes guardarlo con normalidad.
                </p>
              </div>
            )}

            {/* Mascota nombrada en el documento que aún no está registrada */}
            {petMatch?.data?.nombre && petMatch.candidates.length === 0 && !linkedPet && petNueva !== "no" && (
              <div className="mt-4 bg-[#EFF6F1] border border-[#CDE3D6] rounded-2xl p-4" data-testid="pet-nueva-card">
                <h3 className="font-heading font-bold text-base text-[#1C2421] flex items-center gap-2"><PawPrint size={20} className="text-[#2E5645] shrink-0" />
                  Este documento parece corresponder a una mascota llamada {petMatch.data.nombre}. ¿Quieres registrarla y asociar este gasto?</h3>
                <div className="flex flex-col gap-2 mt-3">
                  <button data-testid="pet-nueva-registrar" disabled={petNueva === "creando"} onClick={registrarMascota}
                    className="w-full min-h-[48px] rounded-xl bg-[#3A6B56] text-white font-semibold text-base hover:bg-[#2E5645] disabled:opacity-60">
                    {petNueva === "creando" ? "Registrando…" : `Registrar a ${petMatch.data.nombre} y asociar`}
                  </button>
                  {pets.length > 0 && (
                    <button data-testid="pet-nueva-existente" onClick={() => setPetNueva("elegir")}
                      className="w-full min-h-[44px] rounded-xl bg-white text-[#2E5645] font-semibold text-base border border-[#CDE3D6]">Asociar a una mascota existente</button>
                  )}
                  {petNueva === "elegir" && pets.map((p) => (
                    <button key={p.id} data-testid={`pet-nueva-elegir-${p.id}`} onClick={() => linkToPet(p)}
                      className="w-full min-h-[44px] rounded-xl bg-white text-[#1C2421] font-semibold border border-[#E5DFD5] flex items-center justify-center gap-2"><Link2 size={16} /> {p.name}</button>
                  ))}
                  <button data-testid="pet-nueva-sin-asociar" onClick={() => { setPetNueva("no"); if (form.category === "Mascotas") set("category", "Otros"); }}
                    className="w-full min-h-[44px] rounded-xl bg-white text-[#4A5550] font-semibold text-sm border border-[#E5DFD5]">Guardar sin asociar</button>
                </div>
              </div>
            )}

            {/* Vinculación automática con una mascota existente (FASE 6) */}
            {petMatch && petMatch.candidates.length > 0 && !linkedPet && !linkDismissed && (
              <div className="mt-4 bg-[#EFF6F1] border border-[#CDE3D6] rounded-2xl p-4" data-testid="pet-link-card">
                <div className="flex items-center gap-2 text-[#2E5645]">
                  <PawPrint size={20} />
                  <h3 className="font-heading font-bold text-base text-[#1C2421]">
                    Este documento parece corresponder a {petMatch.best.pet.name}
                  </h3>
                </div>
                <p className="text-sm text-[#4A5550] mt-1.5">
                  Hemos encontrado una coincidencia en:{" "}
                  <span className="font-semibold text-[#2E5645]">{reasonsText(petMatch.best.reasons)}</span>.
                </p>
                <ConfidenceBadge level={petMatch.best.confidence} />
                {!showPicker ? (
                  <div className="flex flex-col gap-2 mt-3">
                    <button
                      data-testid="pet-link-confirm"
                      onClick={() => linkToPet(petMatch.best.pet)}
                      className="w-full min-h-[48px] rounded-xl bg-[#3A6B56] text-white font-semibold text-base hover:bg-[#2E5645] flex items-center justify-center gap-2"
                    >
                      <Link2 size={18} /> Sí, vincular con {petMatch.best.pet.name}
                    </button>
                    <div className="flex gap-2">
                      <button
                        data-testid="pet-link-other"
                        onClick={() => setShowPicker(true)}
                        className="flex-1 min-h-[44px] rounded-xl bg-white text-[#4A5550] font-semibold text-sm border border-[#E5DFD5] hover:bg-[#F2EDE4]"
                      >
                        Elegir otra mascota
                      </button>
                      <button
                        data-testid="pet-link-none"
                        onClick={() => setLinkDismissed(true)}
                        className="flex-1 min-h-[44px] rounded-xl bg-white text-[#4A5550] font-semibold text-sm border border-[#E5DFD5] hover:bg-[#F2EDE4]"
                      >
                        No vincular
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3">
                    <Label>Elige la mascota</Label>
                    <select
                      data-testid="pet-link-picker"
                      defaultValue=""
                      onChange={(e) => {
                        const p = pets.find((x) => x.id === e.target.value);
                        if (p) linkToPet(p);
                      }}
                      className={inputCls}
                    >
                      <option value="" disabled>Selecciona…</option>
                      {pets.map((p) => <option key={p.id} value={p.id}>{p.name}{p.species ? ` · ${p.species}` : ""}</option>)}
                    </select>
                    <button
                      onClick={() => setShowPicker(false)}
                      className="mt-2 text-sm font-semibold text-[#6E7A74]"
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Confirmación de vinculación + propuesta de actualización de ficha */}
            {linkedPet && (
              <div className="mt-4" data-testid="pet-linked-box">
                <div className="flex items-center gap-2 bg-[#E8F0EC] border border-[#CDE3D6] rounded-xl px-4 py-3">
                  <PawPrint size={18} className="text-[#2E5645] shrink-0" />
                  <p className="text-sm text-[#2E5645] flex-1">
                    Se vinculará a <span className="font-semibold">{linkedPet.name}</span> al confirmar.
                  </p>
                  <button
                    data-testid="pet-unlink"
                    onClick={() => { setLinkedPet(null); setForm((f) => ({ ...f, pet_id: "" })); setPetUpdates([]); setPetUpdated(false); }}
                    className="text-sm font-semibold text-[#B91C1C]"
                  >
                    Quitar
                  </button>
                </div>
                {petUpdates.length > 0 && !petUpdated && (
                  <PetUpdateProposal
                    petName={linkedPet.name}
                    changes={petUpdates}
                    busy={updBusy}
                    onApply={applyPetUpdates}
                    onSkip={() => setPetUpdated(true)}
                  />
                )}
                {petUpdated && petUpdates.length > 0 && (
                  <p className="text-sm text-[#2E5645] mt-2 flex items-center gap-1.5">
                    <FileCheck2 size={16} /> Ficha de {linkedPet.name} revisada.
                  </p>
                )}
              </div>
            )}

            {/* Duda vivienda/persona (FASE 9) */}
            {dudaVivPersona && (
              <div className="mt-4 bg-[#FFF7ED] border border-[#FED7AA] rounded-2xl p-4" data-testid="duda-viv-persona-card">
                <div className="flex items-center gap-2 text-[#9A3412]">
                  <HelpCircle size={20} />
                  <h3 className="font-heading font-bold text-base text-[#1C2421]">
                    No estoy seguro de dónde encaja este documento
                  </h3>
                </div>
                <p className="text-sm text-[#4A5550] mt-1.5">¿Quieres asociarlo a Ocio y Reservas, una vivienda, vehículo, mascota o persona?</p>
                <div className="flex flex-col gap-2 mt-3">
                  <button data-testid="duda-elegir-ocio" onClick={() => setOcioDoc({ id: docId, filename: origName })}
                    className="w-full min-h-[48px] rounded-xl bg-white text-[#2E5645] font-semibold text-base border border-[#CDE3D6] hover:bg-[#EFF6F1] flex items-center justify-center gap-2">
                    <Ticket size={18} /> Es una entrada, reserva o billete (Ocio y Reservas)
                  </button>
                  <button data-testid="duda-elegir-vivienda"
                    onClick={() => { set("category", "Hogar"); setDudaChoice("vivienda"); }}
                    className="w-full min-h-[48px] rounded-xl bg-[#3A6B56] text-white font-semibold text-base hover:bg-[#2E5645] flex items-center justify-center gap-2">
                    <Home size={18} /> Es de una vivienda
                  </button>
                  <button data-testid="duda-elegir-persona"
                    onClick={() => { set("category", "Contratos y seguros"); set("vivienda_id", ""); setLinkedViv(null); setDudaChoice("persona"); }}
                    className="w-full min-h-[48px] rounded-xl bg-white text-[#2E5645] font-semibold text-base border border-[#CDE3D6] hover:bg-[#EFF6F1] flex items-center justify-center gap-2">
                    <User size={18} /> Es de una persona
                  </button>
                  {[["vehiculo", "Vehículos", "Es de un vehículo", Car], ["mascota", "Mascotas", "Es de una mascota", PawPrint]].map(([id, c, l, I]) => (
                    <button key={id} data-testid={`duda-elegir-${id}`}
                      onClick={() => { set("category", c); set("vivienda_id", ""); setLinkedViv(null); setDudaChoice(id); }}
                      className="w-full min-h-[48px] rounded-xl bg-white text-[#2E5645] font-semibold text-base border border-[#CDE3D6] hover:bg-[#EFF6F1] flex items-center justify-center gap-2">
                      <I size={18} /> {l}
                    </button>
                  ))}
                  <button data-testid="duda-dejar-pendiente"
                    onClick={() => { setDudaChoice("pendiente"); setVivDismissed(true); }}
                    className="w-full min-h-[44px] rounded-xl bg-transparent text-[#6E7A74] font-semibold text-sm hover:bg-[#F2EDE4]">
                    Dejar pendiente
                  </button>
                </div>
              </div>
            )}

            {dudaChoice === "persona" && (
              <div className="mt-4 flex items-center gap-2 bg-[#EFF6F1] border border-[#CDE3D6] rounded-xl px-4 py-3" data-testid="duda-persona-box">
                <User size={18} className="text-[#2E5645] shrink-0" />
                <p className="text-sm text-[#2E5645]">Se guardará en <span className="font-semibold">Contratos y servicios personales</span>. Elige la persona más abajo.</p>
              </div>
            )}

            {/* Varias viviendas posibles (FASE 9) */}
            {variasViviendas && (
              <div className="mt-4 bg-[#EFF6F1] border border-[#CDE3D6] rounded-2xl p-4" data-testid="varias-viviendas-card">
                <div className="flex items-center gap-2 text-[#2E5645]">
                  <Home size={20} />
                  <h3 className="font-heading font-bold text-base text-[#1C2421]">
                    He detectado varias posibles viviendas
                  </h3>
                </div>
                <p className="text-sm text-[#4A5550] mt-1.5">¿A cuál corresponde este documento?</p>
                <div className="space-y-2 mt-3">
                  {vivMatch.candidates.map((c) => (
                    <button key={c.viv.id} data-testid={`varias-viv-${c.viv.id}`} onClick={() => elegirVivienda(c.viv)}
                      className="w-full text-left bg-white border border-[#E5DFD5] rounded-xl p-3 hover:border-[#3A6B56] transition-colors">
                      <p className="font-semibold text-base text-[#1C2421]">{c.viv.alias}</p>
                      <p className="text-sm text-[#6E7A74]">{c.viv.direccion || c.viv.municipio || "Sin dirección"}</p>
                      {c.reasons.length > 0 && (
                        <p className="text-xs text-[#2E5645] mt-0.5">Coincide en: {reasonsText(c.reasons)}</p>
                      )}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2 mt-3">
                  <button data-testid="varias-viv-crear" onClick={crearVivDesdeDoc} disabled={vivCreating}
                    className="flex-1 min-h-[44px] rounded-xl bg-[#3A6B56] text-white font-semibold text-sm hover:bg-[#2E5645] disabled:opacity-60 flex items-center justify-center gap-1.5">
                    <Home size={16} /> {vivCreating ? "Creando…" : "Crear vivienda"}
                  </button>
                  <button data-testid="varias-viv-pendiente" onClick={() => setVivDismissed(true)}
                    className="flex-1 min-h-[44px] rounded-xl bg-white text-[#4A5550] font-semibold text-sm border border-[#E5DFD5] hover:bg-[#F2EDE4]">
                    Dejar pendiente
                  </button>
                </div>
              </div>
            )}

            {/* Vinculación automática con un vehículo (FASE 1 · Vehículos) */}
            {vehMatch && !linkedVeh && !vehDismissed && (vehMatch.candidates.length > 0 || vehMatch.data.matricula || vehMatch.data.vin) && (
              <div className="mt-4 bg-[#EFF6F1] border border-[#CDE3D6] rounded-2xl p-4" data-testid="veh-link-card">
                <div className="flex items-center gap-2 text-[#2E5645]">
                  <Car size={20} />
                  <h3 className="font-heading font-bold text-base text-[#1C2421]">
                    {vehMatch.best ? `Este documento parece corresponder a ${vehMatch.best.veh.alias}` : "No encuentro este vehículo"}
                  </h3>
                </div>
                {vehMatch.best ? (
                  <p className="text-sm text-[#4A5550] mt-1.5">
                    Coincidencia en: <span className="font-semibold text-[#2E5645]">{reasonsText(vehMatch.best.reasons)}</span>. ¿Quieres vincularlo?
                  </p>
                ) : (
                  <p className="text-sm text-[#4A5550] mt-1.5">
                    {[vehMatch.data.matricula && `matrícula ${vehMatch.data.matricula}`, vehMatch.data.marca, vehMatch.data.modelo].filter(Boolean).join(" · ")}. ¿Quieres añadirlo?
                  </p>
                )}
                <div className="flex flex-col gap-2 mt-3">
                  {vehMatch.best && vehMatch.candidates.map((c) => (
                    <button key={c.veh.id} data-testid={`veh-link-${c.veh.id}`} onClick={() => linkToVeh(c.veh)}
                      className="w-full min-h-[48px] rounded-xl bg-[#3A6B56] text-white font-semibold text-base hover:bg-[#2E5645] flex items-center justify-center gap-2">
                      <Link2 size={18} /> Vincular con {c.veh.alias}
                    </button>
                  ))}
                  <button data-testid="veh-link-crear" onClick={crearVehDesdeDoc} disabled={vehCreating}
                    className="w-full min-h-[48px] rounded-xl bg-white text-[#2E5645] font-semibold text-base border border-[#CDE3D6] hover:bg-[#EFF6F1] disabled:opacity-60 flex items-center justify-center gap-2">
                    <Plus size={18} /> {vehCreating ? "Creando…" : "Añadir vehículo nuevo"}
                  </button>
                  <button data-testid="veh-link-dismiss" onClick={() => setVehDismissed(true)}
                    className="w-full min-h-[44px] rounded-xl bg-transparent text-[#6E7A74] font-semibold text-sm hover:bg-[#F2EDE4]">
                    Dejar pendiente
                  </button>
                </div>
              </div>
            )}

            {linkedVeh && (
              <div className="mt-4 flex items-center gap-2 bg-[#EFF6F1] border border-[#CDE3D6] rounded-xl px-4 py-3" data-testid="veh-linked-box">
                <Car size={18} className="text-[#2E5645] shrink-0" />
                <p className="text-sm text-[#2E5645]">Vinculado a <span className="font-semibold">{linkedVeh.alias}</span>.</p>
              </div>
            )}


            {/* Vinculación automática con una vivienda existente (FASE 6 · Hogar) */}
            {vivMatch && !linkedViv && !vivDismissed && !dudaVivPersona && !variasViviendas && dudaChoice !== "persona" && dudaChoice !== "pendiente" && (vivMatch.candidates.length > 0 || vivMatch.data.primaryAddress) && (
              <div className="mt-4 bg-[#EFF6F1] border border-[#CDE3D6] rounded-2xl p-4" data-testid="viv-link-card">
                <div className="flex items-center gap-2 text-[#2E5645]">
                  <Home size={20} />
                  <h3 className="font-heading font-bold text-base text-[#1C2421]">
                    {vivMatch.best
                      ? `Este documento parece corresponder a ${vivMatch.best.viv.alias}`
                      : "No encuentro una vivienda con esta dirección"}
                  </h3>
                </div>
                {vivMatch.best ? (
                  <>
                    <p className="text-sm text-[#4A5550] mt-1.5">
                      Coincidencia en: <span className="font-semibold text-[#2E5645]">{reasonsText(vivMatch.best.reasons)}</span>.
                    </p>
                    <ConfidenceBadge level={vivMatch.best.confidence} />
                  </>
                ) : (
                  <p className="text-sm text-[#4A5550] mt-1.5">
                    Dirección detectada: <span className="font-semibold text-[#2E5645]">{vivMatch.data.primaryAddress}</span>.
                  </p>
                )}

                {!showVivPicker ? (
                  <div className="flex flex-col gap-2 mt-3">
                    {vivMatch.best ? (
                      <button data-testid="viv-link-confirm" onClick={() => linkToViv(vivMatch.best.viv)}
                        className="w-full min-h-[48px] rounded-xl bg-[#3A6B56] text-white font-semibold text-base hover:bg-[#2E5645] flex items-center justify-center gap-2">
                        <Link2 size={18} /> Sí, vincular con {vivMatch.best.viv.alias}
                      </button>
                    ) : (
                      <button data-testid="viv-create" onClick={crearVivDesdeDoc} disabled={vivCreating}
                        className="w-full min-h-[48px] rounded-xl bg-[#3A6B56] text-white font-semibold text-base hover:bg-[#2E5645] disabled:opacity-60 flex items-center justify-center gap-2">
                        <Home size={18} /> {vivCreating ? "Creando…" : "Crear vivienda con esta dirección"}
                      </button>
                    )}
                    <div className="flex gap-2">
                      {viviendas.length > 0 && (
                        <button data-testid="viv-link-other" onClick={() => setShowVivPicker(true)}
                          className="flex-1 min-h-[44px] rounded-xl bg-white text-[#4A5550] font-semibold text-sm border border-[#E5DFD5] hover:bg-[#F2EDE4]">
                          {vivMatch.best ? "Elegir otra vivienda" : "Elegir vivienda"}
                        </button>
                      )}
                      <button data-testid="viv-link-none" onClick={() => setVivDismissed(true)}
                        className="flex-1 min-h-[44px] rounded-xl bg-white text-[#4A5550] font-semibold text-sm border border-[#E5DFD5] hover:bg-[#F2EDE4]">
                        No vincular
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3">
                    <Label>¿A qué vivienda corresponde?</Label>
                    <select data-testid="viv-link-picker" defaultValue=""
                      onChange={(e) => { const v = viviendas.find((x) => x.id === e.target.value); if (v) linkToViv(v); }}
                      className={inputCls}>
                      <option value="" disabled>Selecciona…</option>
                      {viviendas.map((v) => <option key={v.id} value={v.id}>{v.alias}{v.municipio ? ` · ${v.municipio}` : ""}</option>)}
                    </select>
                    <button onClick={() => setShowVivPicker(false)} className="mt-2 text-sm font-semibold text-[#6E7A74]">Cancelar</button>
                  </div>
                )}
              </div>
            )}

            {servicioHogar && (
              <div className="mt-4 bg-[#EFF6F1] border border-[#CDE3D6] rounded-2xl p-4" data-testid="viv-servicio-card">
                <div className="flex items-center gap-2 text-[#2E5645]">
                  <Home size={20} />
                  <h3 className="font-heading font-bold text-base text-[#1C2421]">Este documento parece estar relacionado con una vivienda. ¿Quieres asignarlo?</h3>
                </div>
                <div className="flex flex-col gap-2 mt-3">
                  {viviendas.map((v) => (
                    <button key={v.id} type="button" data-testid={`viv-servicio-${v.id}`} onClick={() => linkToViv(v)}
                      className="w-full min-h-[48px] rounded-xl bg-white text-[#1C2421] font-semibold text-base border border-[#CDE3D6] hover:bg-[#F2EDE4] flex items-center justify-center gap-2">
                      <Link2 size={18} /> {v.alias}{v.municipio ? ` · ${v.municipio}` : ""}
                    </button>
                  ))}
                  <button type="button" data-testid="viv-servicio-sin-asignar" onClick={() => { setVivDismissed(true); setVivSinAsignar(true); }}
                    className="w-full min-h-[44px] rounded-xl bg-white text-[#4A5550] font-semibold text-sm border border-[#E5DFD5] hover:bg-[#F2EDE4]">
                    Sin asignar
                  </button>
                </div>
              </div>
            )}

            {/* Confirmación de vinculación de vivienda + propuesta de actualización */}
            {linkedViv && (
              <div className="mt-4" data-testid="viv-linked-box">
                <div className="flex items-center gap-2 bg-[#E8F0EC] border border-[#CDE3D6] rounded-xl px-4 py-3">
                  <Home size={18} className="text-[#2E5645] shrink-0" />
                  <p className="text-sm text-[#2E5645] flex-1">
                    Se guardará en <span className="font-semibold">Hogar › {linkedViv.alias}</span> al confirmar.
                  </p>
                  <button data-testid="viv-unlink"
                    onClick={() => { setLinkedViv(null); setForm((f) => ({ ...f, vivienda_id: "" })); setVivUpdates([]); setVivUpdated(false); }}
                    className="text-sm font-semibold text-[#B91C1C]">Quitar</button>
                </div>
                {vivUpdates.length > 0 && !vivUpdated && (
                  <PetUpdateProposal entityName={linkedViv.alias} idPrefix="viv-update" changes={vivUpdates}
                    busy={vivUpdBusy} onApply={applyVivUpdates} onSkip={() => setVivUpdated(true)} />
                )}
              </div>
            )}

            <div className="mt-5 space-y-3.5">
              <TextField label="Título del asunto" testid="doc-field-title"
                value={form.title} onChange={(v) => set("title", v)} />
              {esTecnico(origName) && form.title.trim() && (
                <p data-testid="doc-nombre-claro" className="-mt-1.5 text-sm text-[#4A5550] bg-[#E8F0EC] rounded-xl px-3.5 py-2.5 break-words">
                  Tu archivo se llama «{origName}». Lo guardaremos como: <strong>{form.title.trim()}</strong>. Puedes cambiarlo arriba.
                </p>
              )}

              <div>
                <Label>Categoría</Label>
                <select data-testid="doc-field-category" value={form.category}
                  onChange={(e) => (e.target.value === "Ocio" ? setOcioDoc({ id: docId, filename: origName }) : set("category", e.target.value))} className={inputCls}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
                  <option value="Ocio">Ocio y Reservas</option>
                </select>
              </div>

              <TextField label="Entidad / proveedor" testid="doc-field-provider"
                value={form.provider} onChange={(v) => set("provider", v)} warn={verify.provider} />

              <TextField label="Tipo de documento" testid="doc-field-type"
                value={form.matter_type} onChange={(v) => set("matter_type", v)} />

              {personaSug && form.family_member_id !== personaSug.id && (
                <div className="bg-[#E8F0EC] rounded-xl p-3" data-testid="doc-persona-sugerencia">
                  <p className="text-base text-[#1C2421]">Este documento parece corresponder a <b>{personaSug.name}</b>. ¿Quieres asignárselo?</p>
                  <div className="flex gap-2 mt-2">
                    <button type="button" data-testid="doc-persona-sugerencia-aceptar" onClick={() => set("family_member_id", personaSug.id)}
                      className="min-h-[44px] px-4 rounded-xl bg-[#3A6B56] text-white font-semibold">Sí, asignar a {personaSug.name}</button>
                    <button type="button" data-testid="doc-persona-sugerencia-rechazar" onClick={() => setPersonaSug(null)}
                      className="min-h-[44px] px-4 rounded-xl border border-[#E5DFD5] bg-white text-[#1C2421] font-semibold">No</button>
                  </div>
                </div>
              )}
              <PersonSelect testid="doc-field-family" value={form.family_member_id}
                onChange={(v) => set("family_member_id", v)}
                familyMembers={familyMembers} createFamiliar={createFamiliar} />

              {form.category === "Mascotas" && pets.length > 0 && (
                <div>
                  <Label>Mascota relacionada</Label>
                  <select data-testid="doc-field-pet" value={form.pet_id}
                    onChange={(e) => set("pet_id", e.target.value)} className={inputCls}>
                    <option value="">Ninguna</option>
                    {pets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              )}

              <TextField label="Vehículo / inmueble relacionado" testid="doc-field-related"
                value={form.related_to} onChange={(v) => set("related_to", v)} />

              <div className="grid grid-cols-2 gap-3">
                <TextField label="Fecha de vencimiento" type="date" testid="doc-field-due"
                  value={form.due_date} onChange={(v) => set("due_date", v)} warn={verify.due_date} />
                <TextField label="Periodicidad" testid="doc-field-periodicity"
                  value={form.periodicity} onChange={(v) => set("periodicity", v)} warn={verify.periodicidad} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <TextField label="Importe (€)" type="text" testid="doc-field-amount"
                  value={form.amount} onChange={(v) => set("amount", v)} warn={verify.amount} />
                <TextField label="Importe anterior (€)" type="text" testid="doc-field-prev-amount"
                  value={form.previous_amount} onChange={(v) => set("previous_amount", v)} />
              </div>

              <label className="flex items-center gap-3 bg-white border border-[#E5DFD5] rounded-xl px-4 py-3 cursor-pointer">
                <input type="checkbox" data-testid="doc-field-autorenew" checked={!!form.auto_renew}
                  onChange={(e) => set("auto_renew", e.target.checked)}
                  className="w-5 h-5 accent-[#3A6B56]" />
                <span className="text-base text-[#1C2421]">Renovación automática</span>
                {verify.renovacion_automatica && <VerifyTag />}
              </label>

              <TextField label="Información relevante" testid="doc-field-relevant"
                value={form.relevant_info} onChange={(v) => set("relevant_info", v)} />

              <TextField label="Posible próxima acción" testid="doc-field-next-action"
                value={form.next_action} onChange={(v) => set("next_action", v)} />

              <div className="bg-[#E8F0EC] rounded-2xl p-4">
                {/* Con avisos de vencimiento activos y sin aviso propio: no se muestra «Avísame el» vacío, sino las fechas reales. */}
                {conAvisosVenc ? (
                  <div data-testid="doc-avisos-resumen">
                    <p className="font-heading font-bold text-base text-[#1C2421]">Avisos programados</p>
                    <p className="text-base text-[#1C2421] mt-1">Te avisaremos antes del vencimiento del {fechaLarga(form.due_date)}:</p>
                    <ul className="mt-2 space-y-1">
                      {avisosSel.map((o) => <li key={o} data-testid={`doc-aviso-fecha-${o}`} className="text-base font-semibold text-[#2E5645]">{fechaDMA(sumarDias(form.due_date, -o))} · {o === 1 ? "1 día antes" : `${o} días antes`}</li>)}
                    </ul>
                  </div>
                ) : (<>
                <p className="font-heading font-bold text-base text-[#1C2421] mb-2">Recordatorio propuesto</p>
                <div className="grid grid-cols-1 gap-3">
                  <TextField label="Avísame el" type="date" testid="doc-field-reminder-date" borrable
                    value={form.reminder_date} onChange={(v) => set("reminder_date", v)} />
                  <HoraAviso value={form.reminder_time || ""} onChange={(t) => set("reminder_time", t)} testid="doc-field-reminder-hora" />
                  <TextField label="Texto del aviso (opcional)" testid="doc-field-reminder-text"
                    value={form.reminder_text} onChange={(v) => set("reminder_text", v)}
                    placeholder="Ej. Revisar la prima antes de la renovación" />
                </div>
                <p className="text-xs text-[#2E5645] mt-2">Puedes cambiar la fecha o dejarlo en blanco para no crear recordatorio.</p>
                </>)}
                {form.due_date && (
                  <div className="mt-4" data-testid="doc-avisos-venc">
                    <p className="font-heading font-bold text-base text-[#1C2421]">{conAvisosVenc ? "Elegir avisos" : "Avisos antes del vencimiento"}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {avisosFuturos(form.due_date, AVISO_OPTS).map((o) => {
                        const on = (form.aviso_offsets || []).includes(o);
                        return <button key={o} type="button" data-testid={`doc-aviso-${o}`} aria-pressed={on}
                          onClick={() => set("aviso_offsets", on ? form.aviso_offsets.filter((x) => x !== o) : [...(form.aviso_offsets || []), o])}
                          className={`min-h-[44px] px-4 rounded-full font-semibold text-sm border ${on ? "bg-[#3A6B56] text-white border-[#3A6B56]" : "bg-white text-[#4A5550] border-[#E5DFD5]"}`}>{o === 1 ? "1 día antes" : `${o} días antes`}</button>;
                      })}
                    </div>
                  </div>
                )}
              </div>
              {cambioVenc && (
                <div className="bg-[#FFFBEB] border border-[#FCD34D] rounded-2xl p-4 space-y-3" data-testid="doc-cambio-venc">
                  <p className="text-base text-[#1C2421]">Ya tienes «{cambioVenc.title}» con fecha <b>{fechaLarga(cambioVenc.actual)}</b>. Este documento indica <b>{fechaLarga(cambioVenc.nueva)}</b>.</p>
                  <button data-testid="doc-cambio-venc-actualizar" disabled={busy} onClick={() => confirmar({ actualizar_asunto_id: cambioVenc.asunto_id })} className="w-full min-h-[48px] rounded-xl bg-[#3A6B56] text-white font-semibold">Sí, actualizar la fecha</button>
                  <button data-testid="doc-cambio-venc-mantener" disabled={busy} onClick={() => confirmar({ mantener_asunto_id: cambioVenc.asunto_id })} className="w-full min-h-[48px] rounded-xl border border-[#E5DFD5] bg-white font-semibold">No, mantener la fecha guardada</button>
                </div>
              )}
            </div>

            <div className="mt-4 bg-[#F6F3EE] rounded-xl p-4 flex gap-3">
              <Info size={18} className="text-[#3A6B56] shrink-0 mt-0.5" />
              <p className="text-sm text-[#6E7A74] leading-relaxed">
                Los campos marcados con <span className="font-semibold text-[#B45309]">«Conviene que lo verifiques»</span>{" "}
                no se han leído con total seguridad. Compruébalos antes de confirmar.
              </p>
            </div>

            <div className="mt-5 space-y-2.5">
              {gastoDup && (
                <div data-testid="gasto-dup-card" className="bg-[#FFF7ED] border border-[#FED7AA] rounded-2xl p-4 space-y-3">
                  <p className="font-semibold text-base text-[#1C2421]">Ya tienes un gasto igual registrado.</p>
                  {gastoDup.map((g) => <p key={g.id} className="text-sm text-[#9A3412]" data-testid={`gasto-dup-item-${g.id}`}>{eur(g.importe)} · {g.proveedor || g.concepto || "Gasto"} · {fechaLarga(g.fecha)}</p>)}
                  <button data-testid="gasto-dup-no" disabled={busy} onClick={() => { const d = { modo: "no" }; setGastoDec(d); setGastoDup(null); confirmar({}, d); }} className="w-full min-h-[48px] rounded-xl bg-[#3A6B56] text-white font-semibold">No registrar el gasto</button>
                  <button data-testid="gasto-dup-si" disabled={busy} onClick={() => { const d = { ...gastoDec, forzar: true }; setGastoDec(d); setGastoDup(null); confirmar({}, d); }} className="w-full min-h-[48px] rounded-xl border border-[#E5DFD5] bg-white font-semibold">Registrar de todos modos</button>
                </div>
              )}
              {pideGasto && !gastoDec.modo && <p data-testid="gasto-decision-pendiente" className="text-sm font-semibold text-[#B45309]">Antes de confirmar, elige si quieres registrar también el gasto.</p>}
              <button data-testid="add-confirm-control" onClick={() => confirmar()} disabled={busy || (pideGasto && !gastoDec.modo) || !!gastoDup}
                className="w-full min-h-[56px] rounded-xl bg-[#3A6B56] text-white font-semibold text-lg hover:bg-[#2E5645] transition-colors disabled:opacity-60">
                {busy ? "Guardando…" : "Confirmar y controlar"}
              </button>
              <button data-testid="add-decline" onClick={rechazar} disabled={busy}
                className="w-full min-h-[52px] rounded-xl bg-transparent text-[#B91C1C] font-semibold text-base hover:bg-[#FEF2F2] transition-colors">
                Rechazar y descartar documento
              </button>
            </div>
            </>
            )}
            </>)}
          </div>
        )}
      </div>
    </div>
  );
};

const inputCls =
  "w-full min-h-[52px] px-4 rounded-xl bg-white border border-[#E5DFD5] text-base text-[#1C2421] placeholder-[#9AA39D] focus:outline-none focus:ring-4 focus:ring-[#3A6B56]/20 focus:border-[#3A6B56]";

const Label = ({ children }) => (
  <label className="block text-sm font-semibold text-[#4A5550] mb-1.5">{children}</label>
);

const RELACIONES = ["Yo mismo/a", "Cónyuge / Pareja", "Hijo/a", "Padre / Madre", "Hermano/a", "Otro familiar"];

const PersonSelect = ({ testid, value, onChange, familyMembers, createFamiliar }) => {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [relation, setRelation] = useState("Otro familiar");
  const [busy, setBusy] = useState(false);
  const fms = familyMembers.length ? familyMembers : [{ id: "fm-self", name: "Yo mismo/a", relation: "Titular" }];

  const save = async () => {
    if (!name.trim()) return toast.error("Indica el nombre de la persona.");
    setBusy(true);
    try {
      const nuevo = await createFamiliar({ name: name.trim(), relation });
      onChange(nuevo.id);
      setAdding(false); setName(""); setRelation("Otro familiar");
      toast.success("Persona añadida");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo añadir la persona.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Label>Persona relacionada</Label>
      {!adding ? (
        <ConAvatar id={value}><select data-testid={testid} value={value}
          onChange={(e) => (e.target.value === "__new__" ? setAdding(true) : onChange(e.target.value))}
          className={inputCls}>
          {fms.map((f) => <option key={f.id} value={f.id}>{nombreRel(f)}</option>)}
          <option value="__new__">➕ Añadir una persona nueva…</option>
        </select></ConAvatar>
      ) : (
        <div className="bg-white border border-[#E5DFD5] rounded-xl p-3 space-y-2.5" data-testid={`${testid}-add`}>
          <input data-testid={`${testid}-name`} value={name} placeholder="Nombre de la persona"
            onChange={(e) => setName(e.target.value)} className={inputCls} />
          <select data-testid={`${testid}-relation`} value={relation}
            onChange={(e) => setRelation(e.target.value)} className={inputCls}>
            {RELACIONES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <div className="flex gap-2">
            <button type="button" onClick={() => { setAdding(false); setName(""); }}
              className="flex-1 min-h-[44px] rounded-lg bg-[#F2EDE4] font-semibold text-sm border border-[#E5DFD5]">
              Cancelar
            </button>
            <button type="button" data-testid={`${testid}-save`} onClick={save} disabled={busy}
              className="flex-1 min-h-[44px] rounded-lg bg-[#3A6B56] text-white font-semibold text-sm disabled:opacity-60">
              {busy ? "Guardando…" : "Añadir"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const VerifyTag = () => (
  <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#B45309] bg-[#FEF3C7] rounded-full px-2 py-0.5">
    <AlertTriangle size={12} /> Conviene que lo verifiques
  </span>
);

const reasonsText = (reasons) => {
  if (!reasons || reasons.length === 0) return "";
  if (reasons.length === 1) return reasons[0];
  return reasons.slice(0, -1).join(", ") + " y " + reasons[reasons.length - 1];
};

const ConfidenceBadge = ({ level }) => {
  const map = {
    alta: { t: "Coincidencia muy fiable", c: "text-[#15803D] bg-[#DCFCE7]" },
    media: { t: "Coincidencia probable", c: "text-[#B45309] bg-[#FEF3C7]" },
    baja: { t: "Coincidencia por confirmar", c: "text-[#6E7A74] bg-[#F2EDE4]" },
  };
  const m = map[level] || map.baja;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 mt-2 ${m.c}`}>
      {m.t}
    </span>
  );
};

const TextField = ({ label, value, onChange, placeholder, type = "text", testid, warn, borrable }) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <Label>{label}</Label>
      {warn && <VerifyTag />}
    </div>
    <input type={type} data-testid={testid} value={value ?? ""} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={`${inputCls} ${warn ? "border-[#F5C77E] bg-[#FFFBEB]" : ""}`} />
    {borrable && value && (
      <button type="button" data-testid={`${testid}-clear`} onClick={() => onChange("")}
        className="mt-2 min-h-[44px] px-4 rounded-xl bg-white border border-[#E5DFD5] text-base font-semibold text-[#B91C1C] hover:bg-[#FEF2F2] transition-colors">
        Quitar fecha
      </button>
    )}
  </div>
);
